import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { Loader2 } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-transparent text-sm font-medium transition-[background-color,border-color,color,box-shadow,transform] duration-[var(--duration-fast)] ease-[var(--ease-default)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:outline-2 focus-visible:outline-[var(--color-brand)] focus-visible:outline-offset-2 active:scale-[0.99]",
  {
    variants: {
      variant: {
        default: "cursor-pointer bg-[var(--color-accent)] text-[var(--color-accent-text)] hover:bg-[var(--color-accent-hover)]",
        accent: "cursor-pointer bg-[var(--color-brand)] text-[var(--color-accent-text)] hover:brightness-95",
        destructive:
          "cursor-pointer bg-[var(--color-danger)] text-[var(--color-danger-text)] hover:brightness-95",
        outline:
          "cursor-pointer border-[var(--color-border-strong)] bg-transparent text-foreground hover:bg-[var(--color-bg-overlay)]",
        secondary:
          "cursor-pointer bg-background text-primary hover:bg-[var(--color-bg-surface-hover)]",
        ghost:
          "cursor-pointer text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-overlay)] hover:text-primary",
        link: "cursor-pointer !px-0 text-primary underline-offset-4 hover:text-[var(--color-brand)] hover:underline",
      },
      size: {
        // 44px touch targets — never shrink below h-11 on any viewport.
        default: "h-11 px-4 has-[>svg]:px-3.5",
        sm: "h-11 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-12 px-5 has-[>svg]:px-4",
        icon: "size-11 px-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /**
     * Renders a spinner and blocks further clicks while an async action is in
     * flight. The disabled attribute is force-set so a second tap before React
     * re-renders cannot re-enter the handler; handlers that are already running
     * behind an interactive request must still guard re-entrancy themselves.
     *
     * When `asChild` is set the Radix Slot forwards to a single child element,
     * so the spinner cannot be injected as a sibling (that would make the Slot
     * receive 2 children and throw). `disabled`/`aria-busy` still merge onto
     * the child element through the Slot, so `asChild` buttons stay clickable-
     * blocked while loading even without the spinner.
     */
    loading?: boolean;
  }) {
  const baseProps = {
    "data-slot": "button",
    className: cn(buttonVariants({ variant, size, className })),
    disabled: loading || props.disabled,
    "aria-busy": loading || undefined,
    ...props,
  } as const;

  if (asChild) {
    return <Slot {...baseProps}>{children}</Slot>;
  }

  return (
    <button {...baseProps}>
      {loading ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export { Button, buttonVariants };
